import { z } from 'zod';

export const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Backend `/user/login` response — validated at runtime in api.ts.
// ────────────────────────────────────────────────────────────────────────────

export const backendUserDataSchema = z.object({
  recordId: z.union([z.string(), z.number()]).transform((v) => String(v)),
  guid: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  isUserNew: z.boolean().optional(),
  isProfileComplete: z.boolean().optional(),
});

/** Backend `/user/login` response shape (extras passed through, ignored). */
export const backendLoginResponseSchema = z.object({
  data: z
    .object({
      token: z.string().min(1, 'Missing custom token'),
      userData: backendUserDataSchema,
    })
    .passthrough(),
});

export type BackendUserData = z.infer<typeof backendUserDataSchema>;

// ────────────────────────────────────────────────────────────────────────────
// commodity-mdm-api `GET /me` — drives role-based nav + route guards.
// ────────────────────────────────────────────────────────────────────────────

const adminRoleEnum = z.enum(['admin', 'tech_team', 'business_team']);

export const meDataSchema = z.object({
  recordId: z.string(),
  guid: z.string(),
  uid: z.string(),
  email: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string().nullable(),
  mobile: z.string().nullable(),
  roles: z.array(adminRoleEnum),
});

/**
 * Wrapped in the standard ApiResponseShape (`{ statusCode, message, data, error }`).
 * Only `data` is consumed; the wrapper itself is `passthrough`.
 */
export const meResponseSchema = z.object({
  data: meDataSchema,
});

export type MeData = z.infer<typeof meDataSchema>;
